import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/authService";
import { binService } from "../../services/binService";
import { mergeAnonymousBinIntoBackend, completePendingAgentAssignment } from "../../utils/postAuthMerge";
import { preAuthIntent } from "../../utils/preAuthIntent";
import logo from "../../assets/imgs/jhonks_green_logo-removebg.png";
import loginRegImage from "../../assets/imgs/jhonks-login-reg-img.png";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrUsername: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPasswords, setResetPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearError();

    try {
      await login(formData);
      try {
        await mergeAnonymousBinIntoBackend(binService);
      } catch (mergeErr) {
        console.error("Anonymous bin merge error:", mergeErr);
      }

      const pendingAgentId = preAuthIntent.getPendingAgent();
      if (pendingAgentId) {
        await completePendingAgentAssignment({ binService, navigate });
        return;
      }

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    clearError();
    setShowForgotPassword(true);
    setForgotStep("email");
    setForgotEmail("");
    setOtpCode("");
    setResetToken("");
    setResetPasswords({ newPassword: "", confirmPassword: "" });
    setForgotMessage("");
    setForgotError("");
    setForgotLoading(false);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
  };

  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const response = await authService.requestPasswordReset({
        email: forgotEmail,
      });
      setForgotMessage(response.message);
      setForgotStep("otp");
    } catch (err) {
      setForgotError(
        err.response?.data?.message ||
          "We couldn't send the OTP right now. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const response = await authService.verifyPasswordResetOtp({
        email: forgotEmail,
        otp: otpCode,
      });
      setResetToken(response.data?.resetToken || "");
      setForgotMessage(response.message);
      setForgotStep("reset");
    } catch (err) {
      setForgotError(
        err.response?.data?.message ||
          "We couldn't verify that code. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    if (resetPasswords.newPassword !== resetPasswords.confirmPassword) {
      setForgotError("Passwords do not match. Please re-enter them.");
      setForgotLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword({
        email: forgotEmail,
        resetToken,
        newPassword: resetPasswords.newPassword,
        confirmPassword: resetPasswords.confirmPassword,
      });
      setForgotMessage(response.message);
      setForgotStep("success");
    } catch (err) {
      setForgotError(
        err.response?.data?.message ||
          "We couldn't update your password. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="hidden md:block relative">
          <img
            src={loginRegImage}
            alt="Jhonks recycling preview"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/65 via-green-800/35 to-black/40" />
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <h3 className="text-3xl font-bold leading-tight">Welcome back to Jhonks</h3>
            <p className="mt-3 text-sm text-green-100">
              Manage your materials, connect with nearby agents, and complete sales faster.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={logo}
              alt="Jhonks Logo"
              className="h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to Jhonks
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <Link
              to="/register"
              className="font-medium text-green-600 hover:text-green-500"
            >
              create a new account
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label htmlFor="emailOrUsername" className="sr-only">
                Email Or Username
              </label>
              <input
                id="emailOrUsername"
                name="emailOrUsername"
                type="text"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email or username"
                value={formData.emailOrUsername}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showLoginPassword ? "text" : "password"}
                  required
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowLoginPassword(!showLoginPassword);
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center justify-center z-10 focus:outline-none"
                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                >
                  {showLoginPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={openForgotPassword}
                className="text-sm font-medium text-green-600 hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
              >
                Forgot your password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </div>
        </form>
      </div>

      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
          <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
            <button
              type="button"
              onClick={closeForgotPassword}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded-full"
              aria-label="Close reset password dialog"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 115.05 3.636L10 8.586z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <div className="px-6 py-8">
              <h3 className="text-xl font-semibold text-gray-900">
                Reset your password
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {forgotStep === "email" &&
                  "Enter the email linked to your Jhonks account and we’ll send a one-time password."}
                {forgotStep === "otp" &&
                  "Check your email for the 6-digit code we sent and enter it below."}
                {forgotStep === "reset" &&
                  "Create a new password for your account. Make sure it’s something strong and memorable."}
                {forgotStep === "success" &&
                  "Your password has been updated. You can now sign in with your new credentials."}
              </p>

              {forgotError && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {forgotError}
                </div>
              )}

              {forgotMessage && (
                <div className="mt-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {forgotMessage}
                </div>
              )}

              {forgotStep === "email" && (
                <form className="mt-6 space-y-4" onSubmit={handleForgotEmailSubmit}>
                  <div>
                    <label
                      htmlFor="forgotEmail"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email address
                    </label>
                    <input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 sm:text-sm"
                      placeholder="you@example.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {forgotLoading ? "Sending code..." : "Send reset code"}
                  </button>
                </form>
              )}

              {forgotStep === "otp" && (
                <form className="mt-6 space-y-4" onSubmit={handleOtpSubmit}>
                  <div>
                    <label
                      htmlFor="otpCode"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Enter the 6-digit code
                    </label>
                    <input
                      id="otpCode"
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      required
                      maxLength={6}
                      pattern="[0-9]{6}"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 tracking-widest shadow-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 sm:text-sm"
                      placeholder="••••••"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {forgotLoading ? "Verifying..." : "Verify code"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setForgotStep("email")}
                    className="w-full text-sm font-medium text-green-600 hover:text-green-500 focus:outline-none"
                  >
                    Resend code
                  </button>
                </form>
              )}

              {forgotStep === "reset" && (
                <form
                  className="mt-6 space-y-4"
                  onSubmit={handleResetPasswordSubmit}
                >
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        type={showResetNewPassword ? "text" : "password"}
                        value={resetPasswords.newPassword}
                        onChange={(e) =>
                          setResetPasswords((prev) => ({
                            ...prev,
                            newPassword: e.target.value,
                          }))
                        }
                        required
                        minLength={6}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 sm:text-sm"
                        placeholder="Enter a new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetNewPassword(!showResetNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                        aria-label={showResetNewPassword ? "Hide password" : "Show password"}
                      >
                        {showResetNewPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm new password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showResetConfirmPassword ? "text" : "password"}
                        value={resetPasswords.confirmPassword}
                        onChange={(e) =>
                          setResetPasswords((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        required
                        minLength={6}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 sm:text-sm"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowResetConfirmPassword(!showResetConfirmPassword)
                        }
                        className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                        aria-label={
                          showResetConfirmPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showResetConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {forgotLoading ? "Updating..." : "Update password"}
                  </button>
                </form>
              )}

              {forgotStep === "success" && (
                <div className="mt-6 space-y-4">
                  <button
                    type="button"
                    onClick={() => {
                      closeForgotPassword();
                      setForgotStep("email");
                    }}
                    className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Return to sign in
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default Login;
